import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { ConfirmationDialog } from '../components/ConfirmationDialog';

interface ConfirmationOptions {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface ConfirmationContextType {
  confirm: (message: string) => Promise<boolean>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

export const ConfirmationProvider = ({ children }: { children: ReactNode }) => {
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);

  const confirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setOptions({
        message,
        onConfirm: () => {
          setOptions(null);
          resolve(true);
        },
        onCancel: () => {
          setOptions(null);
          resolve(false);
        },
      });
    });
  }, []);

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      {options && <ConfirmationDialog {...options} />}
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
};